import React, { useEffect, useMemo, useState } from 'react';
import {
    Box, Typography, Button, IconButton, Card, Stack, Chip,
    Pagination, Select, MenuItem, FormControl,
    Dialog, DialogTitle, DialogContent, DialogActions,
    List, ListItemButton, ListItemText, TextField, InputAdornment,
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
    Add as AddIcon,
    Delete as DeleteIcon,
    Edit as EditIcon,
    CloudOff as CloudOffIcon,
    Link as LinkIcon, LinkOff as LinkOffIcon,
    Search as SearchIcon, Assignment as AssignmentIcon,
    Visibility as VisibilityIcon,
    ContentCopy as ContentCopyIcon,
} from '@mui/icons-material';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import { initializeDB } from '../../../config/db';
import { FALL_ARREST_RESCUE_PLAN_STORE, JHA_STORE } from '../../../config/constants';
import { generateUUID } from '../../../utils/uuid';
import fallArrestRescuePlanService from '../../../services/fallArrestRescuePlanService';
import PdfPreviewModal from '../../jha/pdf/PdfPreviewModal';

// Rolling-window settings для "Previously submitted" архива.
// SHOW_LIMIT — сколько максимум показывать в UI.
// KEEP_LIMIT — сколько хранить в IndexedDB; всё старше — удаляется при load().
//   Старые submitted записи всё ещё доступны на бэкенде (PDF можно открыть через
//   admin/{id}/pdf), локальный IDB — только cache для offline-fallback.
const SUBMITTED_SHOW_LIMIT = 5;
const SUBMITTED_KEEP_LIMIT = 10;

// Swal-toast по умолчанию имеет z-index 1060 — ниже чем MUI AppBar (1100),
// поэтому toast перекрывается шапкой. Поднимаем выше всех layer'ов.
const TOAST_DEFAULTS = {
    toast: true,
    position: 'top-end',
    showConfirmButton: false,
    didOpen: () => {
        document.querySelectorAll('.swal2-container').forEach((c) => {
            c.style.zIndex = '13000';
        });
    },
};

export default function FallArrestRescuePlanList() {
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);

    // Link to JHA modal state
    const [linkModalOpen, setLinkModalOpen] = useState(false);
    const [linkTarget, setLinkTarget] = useState(null); // FARP record being linked
    const [jhaOptions, setJhaOptions] = useState([]);
    const [jhaSearch, setJhaSearch] = useState('');
    // Set of jhaLocalId которые уже привязаны к каким-либо FARP (для disable
    // соответствующих опций в Link-to-JHA модалке — один JHA = один FARP).
    const [linkedJhaIds, setLinkedJhaIds] = useState(new Set());

    // PDF preview modal state
    const [pdfModalOpen, setPdfModalOpen] = useState(false);
    const [pdfBlob, setPdfBlob] = useState(null);
    const [pdfFilename, setPdfFilename] = useState('');
    const [pdfLoading, setPdfLoading] = useState(false);

    useEffect(() => {
        load();
        // Refresh after background sync завершил pending → submitted транзит
        const onSynced = () => load();
        window.addEventListener('farpSynced', onSynced);
        return () => window.removeEventListener('farpSynced', onSynced);
    }, []);

    async function load() {
        setLoading(true);
        try {
            const db = await initializeDB();
            if (db.objectStoreNames.contains(FALL_ARREST_RESCUE_PLAN_STORE)) {
                const all = await db.transaction(FALL_ARREST_RESCUE_PLAN_STORE, 'readonly')
                    .objectStore(FALL_ARREST_RESCUE_PLAN_STORE).getAll();

                // Rolling-cleanup: оставляем в IDB только последние KEEP_LIMIT
                // submitted-записей (drafts/pending не трогаем — они должны
                // дожить до подтверждения).
                const submittedSorted = (all || [])
                    .filter(p => p.status === 'submitted')
                    .sort((a, b) =>
                        new Date(b.submittedAt || b.updatedAt || 0) -
                        new Date(a.submittedAt || a.updatedAt || 0)
                    );
                if (submittedSorted.length > SUBMITTED_KEEP_LIMIT) {
                    const toDelete = submittedSorted.slice(SUBMITTED_KEEP_LIMIT);
                    const tx = db.transaction(FALL_ARREST_RESCUE_PLAN_STORE, 'readwrite');
                    await Promise.all(toDelete.map(p => tx.objectStore(FALL_ARREST_RESCUE_PLAN_STORE).delete(p.uuid)));
                    await tx.done;
                }

                // После cleanup перечитываем — отображаем актуальный snapshot
                const fresh = await db.transaction(FALL_ARREST_RESCUE_PLAN_STORE, 'readonly')
                    .objectStore(FALL_ARREST_RESCUE_PLAN_STORE).getAll();
                setItems((fresh || [])
                    .sort((a, b) => new Date(b.updatedAt || b.createdAt || 0) - new Date(a.updatedAt || a.createdAt || 0)));
            }
        } catch (e) {
            console.error('Error loading Fall Arrest Rescue Plans:', e);
        } finally {
            setLoading(false);
        }
    }

    // Раздельные секции: активные (drafts + pending) и архив (submitted)
    const activeItems = useMemo(
        () => items.filter(f => f.status !== 'submitted'),
        [items]
    );
    const submittedItems = useMemo(
        () => items.filter(f => f.status === 'submitted'),
        [items]
    );

    // PDF просмотр submitted FARP — открывает PdfPreviewModal (как в JHA list).
    // Модалка handles desktop (iframe) и iOS (кнопки Open in Safari / Download).
    const handleViewPdf = async (rec) => {
        if (!rec?.id) {
            Swal.fire('Not available', 'PDF is only available for plans synced with the server.', 'info');
            return;
        }
        setPdfBlob(null);
        setPdfFilename(`FARP_${rec.jobCode || 'no_job'}_${(rec.planDate || '').replace(/-/g, '')}.pdf`);
        setPdfLoading(true);
        setPdfModalOpen(true);
        try {
            const axiosInstance = (await import('../../../axiosInstance')).default;
            const resp = await axiosInstance.get(`/fall-arrest-rescue-plan/${rec.id}/pdf`, { responseType: 'blob' });
            setPdfBlob(new Blob([resp.data], { type: 'application/pdf' }));
        } catch (e) {
            setPdfModalOpen(false);
            Swal.fire('Error', 'Failed to load PDF: ' + (e.message || ''), 'error');
        } finally {
            setPdfLoading(false);
        }
    };

    const handleClosePdfModal = () => {
        setPdfModalOpen(false);
        setPdfBlob(null);
    };

    const paginatedItems = useMemo(() => {
        const start = (currentPage - 1) * itemsPerPage;
        return activeItems.slice(start, start + itemsPerPage);
    }, [activeItems, currentPage, itemsPerPage]);

    const totalPages = Math.ceil(activeItems.length / itemsPerPage);

    const handleDelete = async (uuid) => {
        if (!window.confirm('Delete this draft?')) return;
        try {
            const db = await initializeDB();
            await db.transaction(FALL_ARREST_RESCUE_PLAN_STORE, 'readwrite')
                .objectStore(FALL_ARREST_RESCUE_PLAN_STORE).delete(uuid);
            await load();
        } catch (e) { console.error(e); }
    };

    /**
     * "Copy to new draft" — создать новую DRAFT форму с данными из submitted.
     * Не копируем: uuid, status, planDate, submitter*, signature*, jhaLocalId,
     * createdAt/updatedAt/submittedAt, server-side id.
     * Юзер открывается уже на форме с предзаполнеными полями.
     */
    const handleCopyToNewDraft = async (src) => {
        try {
            const now = new Date().toISOString();
            const newUuid = generateUUID();
            const draft = {
                uuid:                newUuid,
                status:              'draft',
                planDate:            dayjs().format('YYYY-MM-DD'),
                jhaLocalId:          null,
                // ─── content copied ──────────────────────────────
                jobCode:             src.jobCode || '',
                jobName:             src.jobName || '',
                jobLocation:         src.jobLocation || '',
                jobDescription:      src.jobDescription || '',
                customerName:        src.customerName || '',
                rescuers:            src.rescuers || [],
                competentPerson:     src.competentPerson || null,
                emergencyContact:    src.emergencyContact || null,
                methodOfContact:     src.methodOfContact || null,
                rescueEquipment:     src.rescueEquipment || null,
                equipmentLocation:   src.equipmentLocation || null,
                anchorPoint:         src.anchorPoint || '',
                landingArea:         src.landingArea || '',
                obstructionsHazards: src.obstructionsHazards || '',
                checklist:           src.checklist || [],
                preWorkTasks:        src.preWorkTasks || [],
                responseProcedure:   src.responseProcedure || [],
                // submitter оставляем (юзер обычно копирует свою же форму);
                // подпись — нет, она привязана к конкретному моменту submit.
                submitterName:       src.submitterName || '',
                submitterSignature:  '',
                submitterSignatureDate: '',
                createdAt:           now,
                updatedAt:           now,
            };
            await fallArrestRescuePlanService.saveDraft(draft);
            Swal.fire({
                ...TOAST_DEFAULTS,
                icon: 'success',
                title: 'Copied — opening new draft',
                timer: 1500,
            });
            navigate(`/safety/forms/fall-arrest-rescue-plan/form?uuid=${newUuid}`);
        } catch (e) {
            Swal.fire('Error', 'Failed to copy: ' + (e.message || 'unknown'), 'error');
        }
    };

    // ─── Link to JHA: open modal, load JHA list from IDB ──────────────
    const openLinkModal = async (rec) => {
        setLinkTarget(rec);
        setJhaSearch('');
        try {
            const db = await initializeDB();

            // 1) Load JHAs
            const all = db.objectStoreNames.contains(JHA_STORE)
                ? await db.transaction(JHA_STORE, 'readonly').objectStore(JHA_STORE).getAll()
                : [];
            const jhas = (all || []).sort((a, b) =>
                new Date(b.stepOne?.date || b.createdAt || 0) - new Date(a.stepOne?.date || a.createdAt || 0)
            );
            setJhaOptions(jhas);

            // 2) Build Set of jhaLocalId которые УЖЕ привязаны к каким-то FARP.
            //    Один JHA → один FARP — нельзя привязать вторую запись на тот же
            //    JHA. Исключаем сам текущий rec (если он уже залинкан — линковка
            //    на тот же JHA это no-op, но не блокировка).
            const farps = db.objectStoreNames.contains(FALL_ARREST_RESCUE_PLAN_STORE)
                ? await db.transaction(FALL_ARREST_RESCUE_PLAN_STORE, 'readonly').objectStore(FALL_ARREST_RESCUE_PLAN_STORE).getAll()
                : [];
            const linkedIds = new Set();
            for (const f of (farps || [])) {
                if (!f?.jhaLocalId) continue;
                if (f.uuid === rec.uuid) continue; // сам себя не считаем
                linkedIds.add(f.jhaLocalId);
            }
            setLinkedJhaIds(linkedIds);
        } catch (e) {
            console.error('Failed to load JHA list:', e);
            setJhaOptions([]);
            setLinkedJhaIds(new Set());
        }
        setLinkModalOpen(true);
    };

    const closeLinkModal = () => {
        setLinkModalOpen(false);
        setLinkTarget(null);
    };

    const handleSelectJha = async (jha) => {
        if (!linkTarget || !jha?.localId) return;
        const ok = await fallArrestRescuePlanService.linkToJha(linkTarget.uuid, jha.localId);
        if (ok) {
            Swal.fire({
                ...TOAST_DEFAULTS,
                icon: 'success',
                title: `Linked to JHA ${jha.stepOne?.jobNumber || ''}`.trim(),
                timer: 2000,
            });
            await load();
        } else {
            Swal.fire('Error', 'Failed to link', 'error');
        }
        closeLinkModal();
    };

    const handleUnlink = async (rec) => {
        const confirm = await Swal.fire({
            title: 'Unlink from JHA?',
            text: 'This Fall Arrest Rescue Plan will no longer be tied to a specific JHA.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, unlink',
            confirmButtonColor: '#d32f2f',
        });
        if (!confirm.isConfirmed) return;
        try {
            const db = await initializeDB();
            const r = await db.get(FALL_ARREST_RESCUE_PLAN_STORE, rec.uuid);
            if (r) {
                r.jhaLocalId = null;
                r.updatedAt = new Date().toISOString();
                await db.put(FALL_ARREST_RESCUE_PLAN_STORE, r);
                await load();
            }
        } catch (e) {
            Swal.fire('Error', 'Failed to unlink: ' + e.message, 'error');
        }
    };

    // Filtered JHA options by search text (job number / site / date).
    //
    // Правила видимости:
    //   1) JHA уже привязан к другому FARP → скрываем (один JHA → один FARP).
    //   2) JHA без jobNumber → скрываем (нечего матчить с FARP'ом который
    //      привязан к конкретному job'у; такие JHA — drafts без context'а).
    //   3) Если FARP имеет jobCode — JHA с другим jobNumber тоже скрываем
    //      (бессмысленно линковать FARP про джоб 7191 к JHA про другой джоб).
    //   4) JHA активный (draft/in_progress/process/...)  → показываем.
    //   5) JHA submitted/completed/closed/finish:
    //        — показываем ТОЛЬКО если: job совпадает И дата JHA ≤ 7 дней.
    //          "Late-link" окно — бригада могла забыть привязать FARP.
    //        — иначе скрываем (поздно линковать).
    const SUBMITTED_JHA_STATUSES = new Set(['submitted', 'completed', 'closed', 'finish']);
    const LATE_LINK_WINDOW_MS = 7 * 24 * 60 * 60 * 1000;
    const filteredJhaOptions = useMemo(() => {
        const q = (jhaSearch || '').trim().toLowerCase();
        const farpJob = linkTarget?.jobCode;
        const now = Date.now();

        const available = jhaOptions.filter(j => {
            // Уже занятые другим FARP — скрываем всегда
            if (j.localId && linkedJhaIds.has(j.localId)) return false;

            // JHA без джоба — скрываем. Они не могут быть связаны с FARP
            // (FARP всегда привязан к джобу).
            const jhaJob = (j.stepOne?.jobNumber || '').toString().trim();
            if (!jhaJob) return false;

            // Если у FARP есть jobCode — JHA должна быть на тот же джоб
            if (farpJob && jhaJob !== farpJob) return false;

            const status = (j.status || '').toString().toLowerCase();
            const isSubmitted = SUBMITTED_JHA_STATUSES.has(status);

            if (!isSubmitted) return true; // активные на same job — показываем

            // Submitted/closed/finish JHA — late-link окно: ≤ 7 дней
            const jhaDateStr = j.stepOne?.date || j.createdAt;
            if (!jhaDateStr) return false;
            const ts = new Date(jhaDateStr).getTime();
            if (Number.isNaN(ts)) return false;
            return (now - ts) <= LATE_LINK_WINDOW_MS;
        });

        // Prioritize JHAs с matching jobCode сверху
        const ranked = available.map(j => ({
            j,
            score: (farpJob && j.stepOne?.jobNumber === farpJob) ? 0 : 1,
        }));
        ranked.sort((a, b) => a.score - b.score);

        if (!q) return ranked.map(x => x.j);
        return ranked
            .map(x => x.j)
            .filter(j => {
                const haystack = [
                    j.stepOne?.jobNumber,
                    j.stepOne?.jobSiteName,
                    j.stepOne?.locationName,
                    j.stepOne?.date,
                    j.localId,
                ].filter(Boolean).join(' ').toLowerCase();
                return haystack.includes(q);
            });
    }, [jhaOptions, jhaSearch, linkTarget, linkedJhaIds]);

    const cardTitle = (rec) => {
        const parts = [];
        if (rec.jobCode) parts.push(rec.jobCode);
        if (rec.jobName) parts.push(rec.jobName);
        return parts.length > 0 ? parts.join(' — ') : (rec.uuid?.slice(0, 8) || 'New plan');
    };

    return (
        <Box sx={{ height: 'calc(100vh - 60px)', backgroundColor: '#f5f5f5', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <Box sx={{ backgroundColor: 'white', borderBottom: '1px solid #e0e0e0', p: 1.5, flexShrink: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <IconButton onClick={() => navigate('/main')} edge="start"><ArrowBackIcon /></IconButton>
                        <Typography variant="h6" fontWeight={600}>Fall Arrest Rescue Plan</Typography>
                    </Box>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => navigate('/safety/forms/fall-arrest-rescue-plan/form')}
                        sx={{ backgroundColor: '#1565c0', '&:hover': { backgroundColor: '#0d47a1' } }}
                    >
                        New Plan
                    </Button>
                </Box>
            </Box>

            <Box sx={{ p: 1.5, flexGrow: 1, overflowY: 'auto' }}>
                {loading ? (
                    <Card sx={{ p: 4 }}><Typography>Loading...</Typography></Card>
                ) : activeItems.length === 0 && submittedItems.length === 0 ? (
                    <Card sx={{ p: 4, textAlign: 'center' }}>
                        <Typography variant="h6" color="text.secondary">No drafts found</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            Click "New Plan" to create one
                        </Typography>
                    </Card>
                ) : (
                    <>
                        <Stack spacing={1}>
                            {paginatedItems.map((rec) => (
                                <Card key={rec.uuid} sx={{ p: 1.5 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Box sx={{ flex: 1 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                                <Typography variant="subtitle1" sx={{ fontSize: '0.95rem' }}>
                                                    {cardTitle(rec)}
                                                </Typography>
                                                {rec.status === 'pending_submit' ? (
                                                    <Chip
                                                        icon={<CloudOffIcon sx={{ fontSize: 14 }} />}
                                                        label="Sync pending"
                                                        size="small"
                                                        sx={{ backgroundColor: '#0288d1', color: 'white', height: 20 }}
                                                        title="Submitted offline — will sync automatically when you reconnect"
                                                    />
                                                ) : (
                                                    <Chip
                                                        label="Draft"
                                                        size="small"
                                                        sx={{ backgroundColor: '#ff9800', color: 'white', height: 20 }}
                                                    />
                                                )}
                                                {rec.jhaLocalId && (
                                                    <Chip
                                                        icon={<AssignmentIcon sx={{ fontSize: 14 }} />}
                                                        label="Linked to JHA"
                                                        size="small"
                                                        variant="outlined"
                                                        sx={{ height: 20, borderColor: '#0288d1', color: '#0288d1' }}
                                                        title={`JHA localId: ${rec.jhaLocalId}`}
                                                    />
                                                )}
                                            </Box>
                                            <Stack direction="row" spacing={2} sx={{ mt: 0.5, flexWrap: 'wrap' }}>
                                                {rec.customerName && (
                                                    <Typography variant="caption" color="text.secondary">
                                                        <strong>Customer:</strong> {rec.customerName}
                                                    </Typography>
                                                )}
                                                {rec.jobLocation && (
                                                    <Typography variant="caption" color="text.secondary">
                                                        <strong>Location:</strong> {rec.jobLocation}
                                                    </Typography>
                                                )}
                                                {rec.planDate && (
                                                    <Typography variant="caption" color="text.secondary">
                                                        <strong>Date:</strong> {rec.planDate}
                                                    </Typography>
                                                )}
                                            </Stack>
                                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
                                                Updated: {new Date(rec.updatedAt || rec.createdAt).toLocaleString()}
                                            </Typography>
                                        </Box>
                                        <Stack direction="row" spacing={0.5}>
                                            <IconButton
                                                size="small"
                                                color="primary"
                                                onClick={() => navigate(`/safety/forms/fall-arrest-rescue-plan/form?uuid=${encodeURIComponent(rec.uuid)}`)}
                                                title="Edit"
                                            >
                                                <EditIcon />
                                            </IconButton>
                                            {rec.jhaLocalId ? (
                                                <IconButton
                                                    size="small"
                                                    color="warning"
                                                    onClick={() => handleUnlink(rec)}
                                                    title="Unlink from JHA"
                                                >
                                                    <LinkOffIcon />
                                                </IconButton>
                                            ) : (
                                                <IconButton
                                                    size="small"
                                                    color="info"
                                                    onClick={() => openLinkModal(rec)}
                                                    title="Link to JHA"
                                                >
                                                    <LinkIcon />
                                                </IconButton>
                                            )}
                                            <IconButton size="small" color="error" onClick={() => handleDelete(rec.uuid)} title="Delete">
                                                <DeleteIcon />
                                            </IconButton>
                                        </Stack>
                                    </Box>
                                </Card>
                            ))}
                        </Stack>

                        {activeItems.length > itemsPerPage && (
                            <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <Typography variant="body2" color="text.secondary">
                                        {((currentPage - 1) * itemsPerPage) + 1}-{Math.min(currentPage * itemsPerPage, activeItems.length)} of {activeItems.length}
                                    </Typography>
                                    <FormControl size="small">
                                        <Select
                                            value={itemsPerPage}
                                            onChange={(e) => { setItemsPerPage(e.target.value); setCurrentPage(1); }}
                                            sx={{ minWidth: 80 }}
                                        >
                                            <MenuItem value={10}>10</MenuItem>
                                            <MenuItem value={25}>25</MenuItem>
                                            <MenuItem value={50}>50</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Box>
                                <Pagination
                                    count={totalPages}
                                    page={currentPage}
                                    onChange={(_, v) => setCurrentPage(v)}
                                    color="primary"
                                    size="medium"
                                    showFirstButton
                                    showLastButton
                                />
                            </Box>
                        )}

                        {/* ─── Submitted archive section ───────────────── */}
                        {submittedItems.length > 0 && (
                            <Box sx={{ mt: 3 }}>
                                <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 600 }}>
                                    Previously submitted ({submittedItems.length})
                                </Typography>
                                <Stack spacing={1} sx={{ mt: 0.5 }}>
                                    {submittedItems.slice(0, SUBMITTED_SHOW_LIMIT).map((rec) => (
                                        <Card key={rec.uuid} sx={{ p: 1.5, backgroundColor: '#fafafa', borderLeft: '3px solid #4caf50' }}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                                        <Typography variant="subtitle2" sx={{ fontSize: '0.9rem' }}>
                                                            {cardTitle(rec)}
                                                        </Typography>
                                                        <Chip
                                                            label="Submitted"
                                                            size="small"
                                                            sx={{ backgroundColor: '#4caf50', color: 'white', height: 18, fontSize: '0.7rem' }}
                                                        />
                                                        {rec.jhaLocalId && (
                                                            <Chip
                                                                icon={<AssignmentIcon sx={{ fontSize: 12 }} />}
                                                                label="JHA"
                                                                size="small"
                                                                variant="outlined"
                                                                sx={{ height: 18, fontSize: '0.7rem' }}
                                                            />
                                                        )}
                                                    </Box>
                                                    <Stack direction="row" spacing={2} sx={{ mt: 0.25, flexWrap: 'wrap' }}>
                                                        {rec.planDate && (
                                                            <Typography variant="caption" color="text.secondary">
                                                                <strong>Date:</strong> {rec.planDate}
                                                            </Typography>
                                                        )}
                                                        {rec.submitterName && (
                                                            <Typography variant="caption" color="text.secondary">
                                                                <strong>By:</strong> {rec.submitterName}
                                                            </Typography>
                                                        )}
                                                    </Stack>
                                                </Box>
                                                <Stack direction="row" spacing={0.5}>
                                                    <IconButton
                                                        size="small"
                                                        color="primary"
                                                        onClick={() => handleCopyToNewDraft(rec)}
                                                        title="Copy to new draft"
                                                    >
                                                        <ContentCopyIcon />
                                                    </IconButton>
                                                    <IconButton
                                                        size="small"
                                                        color="success"
                                                        onClick={() => handleViewPdf(rec)}
                                                        title="View PDF"
                                                    >
                                                        <VisibilityIcon />
                                                    </IconButton>
                                                </Stack>
                                            </Box>
                                        </Card>
                                    ))}
                                </Stack>
                                {submittedItems.length > SUBMITTED_SHOW_LIMIT && (
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                                        Showing latest {SUBMITTED_SHOW_LIMIT} of {submittedItems.length}
                                    </Typography>
                                )}
                            </Box>
                        )}
                    </>
                )}
            </Box>

            {/* ─── Link-to-JHA modal ──────────────────────────────────── */}
            <Dialog open={linkModalOpen} onClose={closeLinkModal} maxWidth="sm" fullWidth>
                <DialogTitle>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LinkIcon color="info" />
                        Link to JHA
                    </Box>
                    {linkTarget && (
                        <Typography variant="caption" color="text.secondary" display="block">
                            FARP {linkTarget.jobCode ? `· ${linkTarget.jobCode}` : ''}
                            {linkTarget.planDate ? ` · ${linkTarget.planDate}` : ''}
                        </Typography>
                    )}
                </DialogTitle>
                <DialogContent dividers>
                    <TextField
                        autoFocus
                        fullWidth
                        size="small"
                        placeholder="Search by job #, site, date…"
                        value={jhaSearch}
                        onChange={(e) => setJhaSearch(e.target.value)}
                        InputProps={{
                            startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
                        }}
                        sx={{ mb: 1.5 }}
                    />
                    {/* ─── Legend: появляется только когда в списке есть late-link JHA ─ */}
                    {filteredJhaOptions.some(j => SUBMITTED_JHA_STATUSES.has((j.status || '').toString().toLowerCase())) && (
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: 'block', mb: 1, lineHeight: 1.3 }}
                        >
                            <Chip
                                label="Submitted — late link"
                                size="small"
                                color="warning"
                                sx={{ height: 16, fontSize: '0.65rem', mr: 0.5, verticalAlign: 'middle' }}
                            />
                            = JHA closed ≤ 7 days ago — still linkable.
                        </Typography>
                    )}

                    {filteredJhaOptions.length === 0 ? (
                        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
                            {jhaSearch
                                ? 'No matching JHA found.'
                                : `No JHA available to link${linkTarget?.jobCode ? ` for job ${linkTarget.jobCode}` : ''}. ` +
                                  'Only same-job JHAs are shown — active ones or recently submitted (≤ 7 days). Already linked JHAs are hidden.'}
                        </Typography>
                    ) : (
                        <List dense disablePadding sx={{ maxHeight: 360, overflowY: 'auto' }}>
                            {filteredJhaOptions.map((j) => {
                                const matchesJob = linkTarget?.jobCode && j.stepOne?.jobNumber === linkTarget.jobCode;
                                const status = (j.status || '').toString().toLowerCase();
                                const isLateLink = SUBMITTED_JHA_STATUSES.has(status);
                                return (
                                    <ListItemButton
                                        key={j.localId || j.id}
                                        onClick={() => handleSelectJha(j)}
                                        sx={{
                                            borderLeft: isLateLink
                                                ? '3px solid #f9a825'
                                                : (matchesJob ? '3px solid #0288d1' : '3px solid transparent'),
                                            mb: 0.5,
                                            borderRadius: 1,
                                            border: '1px solid #e0e0e0',
                                            borderLeftWidth: '3px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                            '&:hover': { backgroundColor: '#f5f7fa' },
                                        }}
                                    >
                                        <ListItemText
                                            sx={{ my: 0 }}
                                            primary={
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                                    <strong>{j.stepOne?.jobNumber || '(no job)'}</strong>
                                                    {j.stepOne?.jobSiteName && (
                                                        <Typography variant="body2" component="span">
                                                            — {j.stepOne.jobSiteName}
                                                        </Typography>
                                                    )}
                                                    {matchesJob && !isLateLink && (
                                                        <Chip label="Same job" size="small" color="info" sx={{ height: 18 }} />
                                                    )}
                                                    {isLateLink && (
                                                        <Chip label="Submitted — late link" size="small" color="warning" sx={{ height: 18 }} />
                                                    )}
                                                </Box>
                                            }
                                            secondary={
                                                <span>
                                                    {j.stepOne?.date && <>Date: {j.stepOne.date} · </>}
                                                    {j.stepOne?.locationName && <>Location: {j.stepOne.locationName} · </>}
                                                    Status: {j.status || '—'}
                                                </span>
                                            }
                                        />
                                        <Button
                                            variant="contained"
                                            size="small"
                                            startIcon={<LinkIcon fontSize="small" />}
                                            sx={{
                                                flexShrink: 0,
                                                backgroundColor: isLateLink ? '#f9a825' : '#1565c0',
                                                '&:hover': { backgroundColor: isLateLink ? '#f57f17' : '#0d47a1' },
                                                textTransform: 'none',
                                                minWidth: 0,
                                                px: 1.25,
                                            }}
                                            // onClick на самом ListItemButton; кнопка визуальный affordance
                                            tabIndex={-1}
                                            disableRipple
                                            component="span"
                                        >
                                            Link
                                        </Button>
                                    </ListItemButton>
                                );
                            })}
                        </List>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeLinkModal}>Cancel</Button>
                </DialogActions>
            </Dialog>

            {/* ─── PDF preview modal (iframe on desktop, Safari open on iOS) ─ */}
            <PdfPreviewModal
                open={pdfModalOpen}
                onClose={handleClosePdfModal}
                pdfBlob={pdfBlob}
                filename={pdfFilename}
                loading={pdfLoading}
            />
        </Box>
    );
}
